import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon, Select, MenuItem } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%'
    }
}));

const Types = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        types: [],
        type: {
            idtype: 0,
            name: '',
            process_required: true
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Types';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/types');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, types: response?.data ?? [] }));
        } else {
            console.log('[Types] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, type: { ...prev.type, [target.name]: target.value } }));

    const validateForm = (type) => {
        const { idtype, name } = state.type;

        switch (type) {
            case 'update':
                const data = state.types.filter(value => value.idtype === idtype);

                if (data[0]) {
                    const before = JSON.stringify(data[0]);
                    const after = JSON.stringify(state.type);
                    if (before !== after) {
                        if (name.trim()) {
                            return true;
                        } else {
                            showToast('All fields is required', 'error');
                            return false;
                        }
                    } else {
                        showToast('Update some field', 'error');
                        return false;
                    }
                } else {
                    showToast('Select some type', 'error');
                    return false;
                }

            case 'store':

                if (name.trim()) {
                    return true;
                } else {
                    showToast('All fields is required', 'error');
                    return false;
                }

            case 'delete':
                if (idtype === 0) {
                    showToast('Select some type', 'error');
                    return false;
                }
                const cond = window.confirm('Are you sure you want to delete this type?');

                return Boolean(idtype && cond);
            default:
                break;
        }
    }

    const clear = () => {
        setState(prev => ({ ...prev, type: { ...prev.type, idtype: 0, name: '', process_required: true } }));
    }

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/type/store', state.type);
            if (response.status === 200) {
                setState(prev => ({ ...prev, types: response?.data ?? prev.types }));
                showToast(response.message);
            } else {
                console.log('[Types] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { idtype } = state.type;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/type/${idtype}/update`, state.type);
            if (response.status === 200) {
                setState(prev => ({ ...prev, types: response?.data ?? prev.types }));
                showToast(response.message);
            } else {
                console.log('[Types] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { idtype } = state.type;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/type/${idtype}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, types: response?.data ?? prev.types }));
                showToast(response.message);
            } else {
                console.log('[Types] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const valueGetter = params => params.value ? 'Yes' : 'No';

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/types/${row.idtype}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'process_required', headerName: 'Process required', flex: 1, valueGetter },
        { field: 'actions', headerName: 'Actions', flex: 0.3, renderCell }
    ];

    if (state.loading) {
        return <Loader />;
    }
    if (!state.permissions.read_attr) {
        return <NotFound />;
    }

    return (
        <div className={classes.root}>
            <ViewTitle title='Types' />
            <div>
                <Grid container>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.type.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>{'Process required'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    value={state.type.process_required}
                                    name='process_required'
                                    onChange={handleChange}
                                >
                                    <MenuItem value={false}>{'No'}</MenuItem>
                                    <MenuItem value={true}>{'Yes'}</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='types' id='idtype' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.types}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idtype}
                    onRowClick={({ row }) => setState(prev => ({ ...prev, type: row }))}
                />
            </div>
        </div>
    );
}

export default Types;